import Dexie, { Table } from 'dexie';
import { v4 as uuidv4 } from 'uuid';
import { IOnboardingState } from '../modules/state-mgmt/onboarding/state';
import { OnboardingStatus } from 'models/general';

// Database Initialization
const dexieInstance = new Dexie('WorkerPortalDB');

// SCHEMA CHANGE: We MUST index 'id' (Worker ID) to perform the safety check
dexieInstance.version(1).stores({
  workers: `
    db.id,           
    db.projectId,    
    id,              
    uploadingDetails.status
  `,
});

export const db = dexieInstance as Dexie & {
  workers: Table<IOnboardingState, string>;
};

// Helpers
export const generateDbId = () => uuidv4();

// Data Access Methods
export const getWorker = async (dbId: string) => {
  return await db.workers.get(dbId);
};

export const getWorkersByProject = async (projectId: string) => {
  return await db.workers.where('db.projectId').equals(projectId).toArray();
};

export const getIncompleteWorkersByProject = async (projectId: string) => {
  const workers = await getWorkersByProject(projectId);
  return workers.filter(worker =>
    [OnboardingStatus.PENDING, OnboardingStatus.PROCESSING, OnboardingStatus.INCOMPLETE].includes(
      worker.uploadingDetails?.status as number,
    ),
  );
};

/**
 * Creates or Updates a worker (Upsert).
 * ROBUST VERSION: Prevents duplicates on API refresh.
 */
export const createOrUpdateWorker = async (workerDetails: IOnboardingState): Promise<IOnboardingState> => {
  const recordToSave: IOnboardingState = {
    ...(workerDetails || {}),
  };

  // 1. Resolve Project ID safely
  const projectId = recordToSave.db?.projectId || recordToSave.temp?.project?.value || '';
  const workerId = recordToSave.id;

  // 2. SAFETY CHECK: If we are missing the DB ID, try to find it first.
  // This handles the "API Refresh" or similar scenarios where we have a worker ID but lost the DB ID.
  if (!recordToSave.db?.id && workerId && projectId) {
    const existingRecord = await db.workers
      .where('id')
      .equals(workerId)
      .filter(w => w.db?.projectId === projectId)
      .first();

    if (existingRecord?.db?.id) {
      // Reuse the existing db object to perform an update instead of a create
      recordToSave.db = existingRecord.db;
    }
  }

  // 3. If it's truly new (still no ID found), generate one.
  if (!recordToSave.db?.id) {
    recordToSave.db = {
      id: generateDbId(),
      projectId: projectId,
      createdByCompanyId: recordToSave.db?.createdByCompanyId,
    };
  }

  await db.workers.put(recordToSave);
  return recordToSave;
};

export const removeWorker = async (dbId: string) => {
  await db.workers.delete(dbId);
};

export const getDatabaseVersion = (): number => {
  return db.verno;
};

/**
 * Update the upload status of a worker in IndexedDB.
 * This is used by the upload manager to track progress.
 */
export const updateWorkerUploadStatus = async (
  dbId: string,
  updates: Partial<IOnboardingState['uploadingDetails']>,
): Promise<void> => {
  const worker = await db.workers.get(dbId);
  if (worker) {
    await db.workers.update(dbId, {
      uploadingDetails: {
        ...worker.uploadingDetails,
        ...updates,
        pendingUploads: {
          ...worker.uploadingDetails?.pendingUploads,
          ...updates.pendingUploads,
        },
      },
      lastUpdatedAt: new Date().toISOString(),
    });
  }
};

/**
 * Get all pending workers for a specific project.
 * Pending workers are those with status = OnboardingStatus.PENDING
 */
export const getPendingWorkersByProject = async (projectId: string): Promise<IOnboardingState[]> => {
  const workers = await getWorkersByProject(projectId);
  return workers.filter(worker => worker.uploadingDetails?.status === OnboardingStatus.PENDING);
};

/**
 * Get all processing workers for a specific project.
 * Used by upload manager to detect orphaned PROCESSING workers from prior sessions.
 */
export const getProcessingWorkersByProject = async (projectId: string): Promise<IOnboardingState[]> => {
  const workers = await getWorkersByProject(projectId);
  return workers.filter(worker => worker.uploadingDetails?.status === OnboardingStatus.PROCESSING);
};

/**
 * Cleanup old workers from IndexedDB based on their status and lastUpdatedAt:
 * - Completed workers: Delete after 24 hours
 * - Incomplete workers: Delete after 14 days
 * - Pending workers: Delete after 14 days
 * - Processing workers: Never delete
 */
export const cleanupOldWorkers = async (projectId: string): Promise<number> => {
  const workers = await getWorkersByProject(projectId);
  const now = new Date().getTime();
  const HOURS_24_IN_MS = 24 * 60 * 60 * 1000; //ONE_DAY_IN_MS;
  const DAYS_14_IN_MS = 14 * HOURS_24_IN_MS;

  let deletedCount = 0;

  for (const worker of workers) {
    if (!worker.lastUpdatedAt || !worker.db?.id) continue;

    const lastUpdated = new Date(worker.lastUpdatedAt).getTime();
    const age = now - lastUpdated;
    const status = worker.uploadingDetails?.status;

    let shouldDelete = false;

    switch (status) {
      case OnboardingStatus.COMPLETED:
        shouldDelete = age > HOURS_24_IN_MS;
        break;
      case OnboardingStatus.INCOMPLETE:
      case OnboardingStatus.PENDING:
        shouldDelete = age > DAYS_14_IN_MS;
        break;
      case OnboardingStatus.PROCESSING:
        // Never delete processing workers
        shouldDelete = false;
        break;
      default:
        shouldDelete = false;
    }

    if (shouldDelete) {
      await removeWorker(worker.db.id);
      deletedCount++;
    }
  }
  return deletedCount;
};
